<template>
  <v-card flat color="#161616" class="rounded-xl border-yt overflow-hidden">
    <div class="d-flex align-center pa-4 border-bottom-yt">
      <v-icon small left color="#3ea6ff">mdi-image-multiple-outline</v-icon>
      <span class="text-subtitle-2 font-weight-bold white--text text-uppercase tracking-widest ml-2">
        Galería de Imágenes
      </span>
     
    </div>

    <v-card-text class="pa-4">
      <v-alert v-if="error" type="error" dense text class="rounded-lg mb-4 caption">
        {{ error }}
      </v-alert>

      <v-row dense>
        <v-col
          v-for="img in producto.imagenes"
          :key="'existente-' + img.id"
          cols="6" sm="4" md="3"
        >
          <v-hover v-slot="{ hover }">
            <v-card flat color="#121212" class="rounded-lg img-card border-yt-thin overflow-hidden">
              <v-img :src="img.small || img.original" aspect-ratio="1" cover>
                <v-chip v-if="esPrincipal(img)" x-small color="amber darken-3" class="principal-badge">
                  <v-icon x-small left>mdi-star</v-icon> PRINCIPAL
                </v-chip>

                <v-fade-transition>
                  <div v-if="hover && !img._eliminando" class="img-overlay d-flex flex-column align-center justify-center pa-2">
                    <v-btn 
                      v-if="!esPrincipal(img)" 
                      x-small 
                      block 
                      color="white" 
                      class="black--text font-weight-bold mb-2"
                      @click="setPrincipal(img)"
                      :loading="img._settingPrincipal"
                    >
                      Destacar
                    </v-btn>
                    <v-btn 
                      icon 
                      color="white" 
                      class="delete-btn"
                      @click="eliminarImagen(img)"
                      :loading="img._eliminando"
                    >
                      <v-icon small>mdi-delete-outline</v-icon>
                    </v-btn>
                  </div>
                </v-fade-transition>
              </v-img>
            </v-card>
          </v-hover>
        </v-col>

        <v-col
          v-if="tieneFeatureImagenes"
          v-for="(prev, idx) in previews"
          :key="'preview-' + idx"
          cols="6" sm="4" md="3"
        >
          <v-card flat color="#121212" class="rounded-lg img-card border-yt-thin overflow-hidden">
            <v-img :src="prev.dataUrl" aspect-ratio="1" cover class="opacity-60">
              <div class="fill-height d-flex flex-column align-center justify-center pa-2">
                <v-progress-circular
                  v-if="prev.uploading"
                  :value="prev.progress"
                  size="30"
                  width="3"
                  color="#3ea6ff"
                >
                  <span style="font-size: 8px" class="white--text">{{ prev.progress }}%</span>
                </v-progress-circular>
                
                <template v-else-if="prev.error">
                  <v-icon color="error" small>mdi-alert-circle</v-icon>
                  <v-btn x-small text color="white" @click="reintentarUno(idx)" class="mt-1">Reintentar</v-btn>
                </template>

                <v-btn v-else icon color="white" @click="removePreview(idx)">
                  <v-icon small>mdi-close</v-icon>
                </v-btn>
              </div>
            </v-img>
          </v-card>
        </v-col>

        <v-col v-if="tieneFeatureImagenes" cols="6" sm="4" md="3">
          <div
            class="upload-placeholder d-flex flex-column align-center justify-center"
            @click="triggerUpload"
          >
            <v-icon color="#333" large>mdi-camera-plus-outline</v-icon>
            <span class="super-caption grey--text mt-2 font-weight-bold">Añadir</span>
          </div>
        </v-col>
      </v-row>

      <v-expand-transition>
        <div v-if="tieneFeatureImagenes && previews.length" class="mt-4 pa-3 bg-black-soft rounded-lg d-flex align-center border-yt-thin">
          <span class="caption white--text font-weight-bold">
            <v-icon x-small left color="#3ea6ff">mdi-cloud-upload</v-icon>
            {{ previews.length }} pendientes
          </span>
          <v-spacer />
          <v-btn x-small text color="grey" class="mr-2" @click="limpiarPreviews" :disabled="subiendo">Cancelar</v-btn>
          <v-btn 
            x-small 
            color="#3ea6ff" 
            class="black--text font-weight-bold" 
            @click="uploadAll" 
            :loading="subiendo"
          >
            Subir todo
          </v-btn>
        </div>
      </v-expand-transition>
    </v-card-text>

    <input ref="fileInput" type="file" multiple accept="image/jpeg,image/png,image/webp" class="d-none" @change="onSelect" />
  </v-card>
</template>

<script>
export default {
  name: "ProductoImagenes",
  props: { producto: { type: Object, required: true } },
  computed: {
    tieneFeatureImagenes() {
      const sus = this.$store.state.auth.suscripcion
      if (!sus || !sus.vigente) return true
      return (sus.features || []).some(f => f.codigo === 'productos.imagenes')
    },
  },
  data() {
    return {
      previews: [],
      subiendo: false,
      loading: false,
      error: null,
    };
  },
  methods: {
    triggerUpload() { this.$refs.fileInput.click(); },
    esPrincipal(img) {
      return img.orden === 1;
    },
    onSelect(e) {
      const MAX_SIZE = 10 * 1024 * 1024; // 10 MB
      const ALLOWED = ['image/jpeg', 'image/png', 'image/webp'];
      const files = Array.from(e.target.files);
      // reset input so re-selecting the same file triggers change again
      e.target.value = '';
      files.forEach(file => {
        if (!ALLOWED.includes(file.type)) {
          this.error = `Formato no soportado: ${file.name}. Usar JPEG, PNG o WEBP.`;
          return;
        }
        if (file.size > MAX_SIZE) {
          this.error = `${file.name} supera el límite de 10 MB.`;
          return;
        }
        const reader = new FileReader();
        reader.onload = (ev) => {
          this.previews.push({ file, dataUrl: ev.target.result, uploading: false, progress: 0, error: null });
        };
        reader.readAsDataURL(file);
      });
    },
    removePreview(idx) { this.previews.splice(idx, 1); },
    limpiarPreviews() { this.previews = []; },
    
    async uploadAll() {
      this.subiendo = true;
      this.error = null;
      await Promise.all(this.previews.map((prev) => this.subirUno(prev)));
      if (!this.previews.some(p => p.error)) {
        this.previews = [];
        this.$emit("refresh-producto");
      }
      this.subiendo = false;
    },

    async subirUno(prev) {
      this.$set(prev, "uploading", true);
      const form = new FormData();
      form.append("imagenes", prev.file);
      try {
        const resp = await this.axios.post(`/productos/productos-empresa/${this.producto.id}/imagenes/`, form, {
          onUploadProgress: (e) => this.$set(prev, "progress", Math.round((e.loaded * 100) / e.total))
        });
        const nuevas = Array.isArray(resp.data) ? resp.data : resp.data.imagenes;
        nuevas.forEach(img => this.producto.imagenes.push(img));
      } catch (err) {
        this.$set(prev, "error", "Error al subir");
      } finally {
        this.$set(prev, "uploading", false);
      }
    },

    async reintentarUno(idx) {
      const prev = this.previews[idx];
      this.$set(prev, "error", null);
      await this.subirUno(prev);
      if (!prev.error) this.previews.splice(idx, 1);
    },

    async eliminarImagen(img) {
      this.$set(img, "_eliminando", true);
      try {
        await this.axios.delete(`/productos/productos-empresa/${this.producto.id}/imagenes/${img.id}/`);
        const idx = this.producto.imagenes.findIndex(i => i.id === img.id);
        if (idx !== -1) this.producto.imagenes.splice(idx, 1);
      } finally { this.$set(img, "_eliminando", false); }
    },

    async setPrincipal(img) {
      this.$set(img, "_settingPrincipal", true);
      try {
        await this.axios.post(`/productos/productos-empresa/${this.producto.id}/set_principal/`, { imagen_id: img.id });
        // Actualizar orden localmente: la seleccionada pasa a 1, el resto a >= 2
        this.producto.imagenes.forEach((i, index) => {
          this.$set(i, "orden", i.id === img.id ? 1 : index + 2);
        });
      } finally { this.$set(img, "_settingPrincipal", false); }
    }
  }
};
</script>

<style scoped lang="scss">
.border-yt { border: 1px solid rgba(255, 255, 255, 0.08) !important; }
.border-yt-thin { border: 1px solid rgba(255, 255, 255, 0.05) !important; }
.border-bottom-yt { border-bottom: 1px solid rgba(255, 255, 255, 0.08) !important; }
.bg-black-soft { background-color: #121212; }

.tracking-widest { letter-spacing: 2px !important; }
.super-caption { font-size: 0.65rem; letter-spacing: 1px; }

.img-card {
  position: relative;
  transition: transform 0.2s ease;
  &:hover { transform: scale(1.02); }
}

.principal-badge {
  position: absolute;
  top: 6px;
  left: 6px;
  z-index: 2;
  font-weight: 900 !important;
  font-size: 0.6rem !important;
}

.img-overlay {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(2px);
}

.upload-placeholder {
  width: 100%;
  aspect-ratio: 1;
  border: 2px dashed rgba(255, 255, 255, 0.05);
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  &:hover {
    background-color: rgba(62, 166, 255, 0.02);
    border-color: rgba(62, 166, 255, 0.2);
  }
}

.delete-btn {
  background: rgba(255, 82, 82, 0.2) !important;
  &:hover { background: rgba(255, 82, 82, 0.4) !important; }
}
</style>